import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { BoxBlock } from '@components/page/BlockRenderer';
import { editingBlock, useBlockEditing, useEditorFrame } from '@components/preview/editGates';
import { useAppContext } from '@/context/AppContext';
import { BLOCK_SELECT_MESSAGE } from '@/blocks/preview';
import { BLOCK_TYPES } from '@/blocks/schema';
import { BOX_REFERENCE_WIDTH, boxShownOn, keepsAspect, screenOf } from '@/slideshow/boxes';
import { useSlideshowExperience } from './ImageViewer/hooks/useSlideshowExperience';
import { calculatePagePercentages } from './spreadLayoutUtils';
import { sheetGuides } from './sheetGrid';
import useBoxDrag from './useBoxDrag';

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const HANDLE_POSITION = {
    nw: { left: 0, top: 0 },
    n: { left: '50%', top: 0 },
    ne: { left: '100%', top: 0 },
    e: { left: '100%', top: '50%' },
    se: { left: '100%', top: '100%' },
    s: { left: '50%', top: '100%' },
    sw: { left: 0, top: '100%' },
    w: { left: 0, top: '50%' },
};
const HANDLE_CURSOR = {
    nw: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize',
    n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize',
};
const VALIGN = { top: 'flex-start', center: 'center', bottom: 'flex-end' };

function postToEditor(message) {
    if (typeof window === 'undefined' || window.parent === window) return;
    window.parent.postMessage(message, window.location.origin);
}

/** The block the editor has selected, kept in step with the inspector. */
function useSelectedBlock(enabled) {
    const [selected, setSelected] = useState('');
    useEffect(() => {
        if (!enabled || typeof window === 'undefined') return undefined;
        const onMessage = (event) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type === BLOCK_SELECT_MESSAGE) setSelected(event.data.blockId || '');
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [enabled]);
    const select = useCallback((blockId) => {
        setSelected(blockId);
        postToEditor({ type: BLOCK_SELECT_MESSAGE, blockId, tileId: '', tileKind: '' });
    }, []);
    return [selected, select];
}

/** Width and height of the sheet, kept up to date. */
function useSheetSize(ref) {
    const [size, setSize] = useState({ width: 0, height: 0 });
    useIsoLayoutEffect(() => {
        const node = ref.current;
        if (!node) return undefined;
        const measure = () => {
            const width = node.clientWidth;
            const height = node.clientHeight;
            setSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
        };
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, [ref]);
    return size;
}

function Handle({ mode, zoom, bind }) {
    const size = 10 / zoom;
    return (
        <span
            aria-hidden="true"
            className="absolute rounded-full border bg-white"
            style={{
                ...HANDLE_POSITION[mode],
                width: size,
                height: size,
                marginLeft: -size / 2,
                marginTop: -size / 2,
                borderWidth: 1.5 / zoom,
                borderColor: 'var(--admin-accent, #7c3aed)',
                cursor: HANDLE_CURSOR[mode],
                pointerEvents: 'auto',
                touchAction: 'none',
            }}
            {...bind}
        />
    );
}

Handle.propTypes = {
    mode: PropTypes.string.isRequired,
    zoom: PropTypes.number.isRequired,
    bind: PropTypes.object.isRequired,
};

function isEmptyText(block) {
    const html = block.type === BLOCK_TYPES.COLUMNS
        ? ((block.cells || []).every((cell) => cell.type === BLOCK_TYPES.RICHTEXT)
            ? (block.cells || []).map((cell) => cell.html || '').join('')
            : null)
        : block.type === BLOCK_TYPES.RICHTEXT ? block.html || '' : null;
    return html !== null && !html.replace(/<[^>]+>|&nbsp;|\s/g, '');
}

function boxStyle(box) {
    return {
        position: 'absolute',
        left: `${box.x}%`,
        top: `${box.y}%`,
        width: `${box.w}%`,
        height: box.h != null ? `${box.h}%` : undefined,
        padding: box.pad ? `${box.pad}px` : undefined,
        background: box.bg || undefined,
        display: box.h != null ? 'flex' : undefined,
        flexDirection: box.h != null ? 'column' : undefined,
        justifyContent: box.h != null ? (VALIGN[box.valign] || VALIGN.top) : undefined,
        overflow: box.h != null ? 'hidden' : undefined,
    };
}

function BoxItem({
    box,
    zoom,
    editable,
    interactive = true,
    inert = false,
    selected,
    onSelect,
    drag,
    blockProps,
    hiddenHere = false,
    hint = true,
    outlined = true,
    typing = false,
    onType,
    onText,
}) {
    const entered = useBlockEditing(editable && !inert ? box.block.id : '');
    const editing = entered || typing;
    const style = boxStyle(box);
    const itemRef = useRef(null);

    // The text editor loads on demand. Once it is there the caret goes to the end of
    // its last text, a spot the editor can map (not after its placeholder).
    useEffect(() => {
        if (!typing) return undefined;
        let frame = 0;
        let tries = 0;
        const focus = () => {
            const field = itemRef.current?.querySelector('[contenteditable="true"]');
            if (!field) {
                tries += 1;
                if (tries < 120) frame = requestAnimationFrame(focus);
                return;
            }
            field.focus();
            const strings = field.querySelectorAll('[data-slate-string]');
            const text = strings[strings.length - 1]?.firstChild;
            if (!text) return;
            const selection = window.getSelection();
            selection?.collapse(text, text.textContent.length);
        };
        focus();
        return () => cancelAnimationFrame(frame);
    }, [typing]);
    const chrome = editable && selected && !editing;
    const outline = outlined && editable && (selected || editing);
    return (
        <div
            ref={itemRef}
            data-box-id={inert ? undefined : box.id}
            data-page-path={inert ? undefined : 'page.block'}
            data-page-id={inert ? undefined : box.block.id}
            className={!inert && (interactive || editable) ? 'pointer-events-auto' : 'pointer-events-none'}
            style={{
                ...style,
                ...(hiddenHere && !selected && { opacity: 0.35 }),
                cursor: editable && !editing ? 'move' : undefined,
                touchAction: editable && !editing ? 'none' : undefined,
                outline: outline ? `${(editing ? 2 : 1.5) / zoom}px ${editing ? 'solid' : 'dashed'} var(--admin-accent, #7c3aed)` : undefined,
                outlineOffset: outline ? 2 / zoom : undefined,
            }}
            onClick={editable ? (event) => event.preventDefault() : undefined}
            onDoubleClick={onType && !editing ? (event) => {
                event.preventDefault();
                event.stopPropagation();
                onType(box.block.id);
            } : undefined}
            onKeyDown={typing ? (event) => {
                if (event.key !== 'Escape') return;
                // Esc finishes the typing; it must not also close the dialog around the sheet.
                event.stopPropagation();
                onType('');
            } : undefined}
            {...(editable && !editing ? {
                ...drag.bind(box),
                onPointerDown: (event) => {
                    if (!selected) onSelect(box.block.id);
                    drag.bind(box).onPointerDown(event);
                },
            } : {})}
        >
            <div style={{ pointerEvents: editable && !editing ? 'none' : undefined, width: '100%', minHeight: editable ? (isEmptyText(box.block) ? '4em' : '1.5em') : undefined }}>
                <BoxBlock block={box.block} inert={inert} anchor={false} onTextEdit={typing ? onText : undefined} {...blockProps} />
            </div>
            {editable && hint && !editing && isEmptyText(box.block) && (
                <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.7)', color: '#4b5563', fontSize: 14 / zoom }}
                >
                    Double-click to type
                </span>
            )}
            {chrome && HANDLES
                .filter((mode) => !keepsAspect(box.block.type) || (mode !== 'n' && mode !== 's'))
                .map((mode) => <Handle key={mode} mode={mode} zoom={zoom} bind={drag.bind(box, mode)} />)}
        </div>
    );
}

BoxItem.propTypes = {
    box: PropTypes.object.isRequired,
    zoom: PropTypes.number.isRequired,
    editable: PropTypes.bool,
    interactive: PropTypes.bool,
    inert: PropTypes.bool,
    selected: PropTypes.bool,
    onSelect: PropTypes.func,
    drag: PropTypes.object.isRequired,
    blockProps: PropTypes.object,
    hiddenHere: PropTypes.bool,
    /** Show "Double-click to type" on an empty text box. */
    hint: PropTypes.bool,
    /** Draw its own outline when selected; off in the editor frame, whose inspector frames the box. */
    outlined: PropTypes.bool,
    /** The box's text is being typed here, outside the editor frame. */
    typing: PropTypes.bool,
    /** (blockId) on a double click to type, '' when the typing ends; omit when the box can't be typed in here. */
    onType: PropTypes.func,
    /** (html) while typing. */
    onText: PropTypes.func,
};

/**
 * Blocks placed on a slideshow sheet, above both pictures. The content is set
 * on a reference sheet BOX_REFERENCE_WIDTH wide and scaled to the real one, so
 * text keeps its place and line breaks at every size, like print.
 *
 * With `onChange` (the editor preview) a box is selected with a click, moved
 * by dragging, resized from its handles and entered for typing with a double
 * click.
 */
export default function SpreadBoxes({
    boxes,
    gap = 0,
    single = false,
    onChange,
    interactive = true,
    inert = false,
    reference = 1,
    pages,
    files,
    theme,
    page,
}) {
    const sheetRef = useRef(null);
    const { width, height } = useSheetSize(sheetRef);
    const { content, theme: siteTheme } = useAppContext();
    const inFrame = useEditorFrame();
    const editable = Boolean(onChange) && !inert;
    // In the editor frame a block is opened through the editor; anywhere else (the spread dialog) right here.
    const local = editable && !inFrame;
    const watch = inFrame && !inert;
    const [selected, select] = useSelectedBlock(editable || watch);
    const [live, setLive] = useState(null);
    const [typing, setTyping] = useState('');

    // The saved list wins again once it changes (the edit came back from the editor).
    useEffect(() => setLive(null), [boxes]);

    const guides = useMemo(
        () => sheetGuides({ single, pageWidth: calculatePagePercentages(gap).pageWidthPercent }),
        [single, gap],
    );
    const replace = useCallback((next) => (boxes || []).map((box) => (box.id === next.id ? next : box)), [boxes]);
    const drag = useBoxDrag({
        sheetRef,
        guides,
        onLive: (next) => setLive(replace(next)),
        onSettle: (next) => {
            if (!next) {
                setLive(null);
                return;
            }
            const list = replace(next);
            setLive(list);
            onChange?.(list);
        },
    });

    const experience = useSlideshowExperience();
    // Thumbnails show every box; the viewer only the ones for this screen, the editor dims the rest.
    const screen = interactive ? screenOf(experience) : null;
    const all = live || boxes || [];
    const shown = editable || !screen ? all : all.filter((box) => boxShownOn(box, screen));
    const listRef = useRef(all);
    listRef.current = all;

    // The text editor hands over what was typed when it loses focus, so it is blurred before it closes.
    const endTyping = useCallback(() => {
        const active = document.activeElement;
        if (active?.isContentEditable && sheetRef.current?.contains(active)) active.blur();
        setTyping('');
    }, []);
    const onType = useCallback((id) => (id ? setTyping(id) : endTyping()), [endTyping]);

    // Typing ends when another box is picked or the typed one goes away.
    useEffect(() => {
        if (typing && (selected !== typing || !all.some((box) => box.block.id === typing))) endTyping();
    }, [typing, selected, all, endTyping]);

    const onText = useCallback((html) => {
        onChange?.(listRef.current.map((box) => (box.block.id === typing
            ? { ...box, block: { ...box.block, html } }
            : box)));
    }, [onChange, typing]);

    // A block selected or open for editing in the editor is let go of once it is
    // no longer on this sheet (the slide moved on), so it doesn't stay open unseen.
    const ids = all.map((box) => box.block.id);
    const idsKey = ids.join(' ');
    const heldRef = useRef([]);
    const selectedRef = useRef(selected);
    selectedRef.current = selected;
    useEffect(() => {
        if (!watch) return;
        const before = heldRef.current;
        const now = idsKey ? idsKey.split(' ') : [];
        heldRef.current = now;
        const gone = (id) => Boolean(id) && before.includes(id) && !now.includes(id);
        if (gone(selectedRef.current) || gone(editingBlock())) select('');
    }, [watch, idsKey, select]);
    useEffect(() => () => {
        const held = heldRef.current;
        if (held.includes(selectedRef.current) || held.includes(editingBlock())) {
            postToEditor({ type: BLOCK_SELECT_MESSAGE, blockId: '', tileId: '', tileKind: '' });
        }
    }, []);

    // Delete removes the selected box and arrow keys nudge it, unless it is being typed in.
    useEffect(() => {
        if (!editable || !selected) return undefined;
        const box = shown.find((entry) => entry.block.id === selected);
        if (!box) return undefined;
        const onKey = (event) => {
            const target = event.target;
            if (target?.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target?.tagName || '')) return;
            if (event.key === 'Delete' || event.key === 'Backspace') {
                event.preventDefault();
                onChange(shown.filter((entry) => entry.id !== box.id));
                return;
            }
            const step = event.shiftKey ? 5 : 0.5;
            const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
            const move = moves[event.key];
            if (!move) return;
            event.preventDefault();
            event.stopPropagation();
            const round = (value) => Math.round(value * 100) / 100;
            onChange(shown.map((entry) => (entry.id === box.id
                ? { ...entry, x: round(entry.x + move[0]), y: round(entry.y + move[1]) }
                : entry)));
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [editable, selected, shown, onChange]);

    // A press on the sheet outside every box lets go of the selection.
    useEffect(() => {
        if (!editable || !selected) return undefined;
        const onDown = (event) => {
            if (event.target?.closest?.('[data-box-id]')) return;
            if (!shown.some((box) => box.block.id === selected)) return;
            if (event.target?.closest?.('[data-editorial-filmstrip]')) return;
            endTyping();
            select('');
        };
        document.addEventListener('pointerdown', onDown, true);
        return () => document.removeEventListener('pointerdown', onDown, true);
    }, [editable, selected, shown, select, endTyping]);

    if (!shown.length) return <div ref={sheetRef} className="pointer-events-none absolute inset-0" aria-hidden="true" />;

    const referenceWidth = BOX_REFERENCE_WIDTH * reference;
    const zoom = width > 0 ? width / referenceWidth : 0;
    const blockProps = {
        page,
        pages: pages || content?.pages || {},
        files: files || content?.files || {},
        theme: theme || siteTheme,
    };

    return (
        <div ref={sheetRef} className="spread-boxes pointer-events-none absolute inset-0" style={{ zIndex: 3 }}>
            {zoom > 0 && (
                <div
                    className="absolute left-0 top-0"
                    style={{
                        width: referenceWidth,
                        height: height / zoom,
                        // Not CSS zoom: the editor places its frames from getBoundingClientRect.
                        transform: `scale(${zoom})`,
                        transformOrigin: '0 0',
                    }}
                >
                    {shown.map((box) => (
                        <BoxItem
                            key={box.id}
                            box={box}
                            zoom={zoom}
                            editable={editable}
                            interactive={interactive}
                            inert={inert}
                            selected={selected === box.block.id}
                            onSelect={select}
                            drag={drag}
                            blockProps={blockProps}
                            hiddenHere={!boxShownOn(box, screen)}
                            hint={!local || box.block.type === BLOCK_TYPES.RICHTEXT}
                            outlined={!inFrame}
                            typing={typing === box.block.id}
                            onType={local && box.block.type === BLOCK_TYPES.RICHTEXT ? onType : undefined}
                            onText={onText}
                        />
                    ))}
                    {drag.lines.x != null && (
                        <span
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-y-0"
                            style={{ left: `${drag.lines.x}%`, width: 1 / zoom, background: 'var(--admin-accent, #7c3aed)' }}
                        />
                    )}
                    {drag.lines.y != null && (
                        <span
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-x-0"
                            style={{ top: `${drag.lines.y}%`, height: 1 / zoom, background: 'var(--admin-accent, #7c3aed)' }}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

SpreadBoxes.propTypes = {
    /** The slide's boxes (lib/slideshow/boxes). */
    boxes: PropTypes.array,
    /** Spread gap, percent of a page's width. */
    gap: PropTypes.number,
    /** A single picture: the sheet is one page. */
    single: PropTypes.bool,
    /** Editor preview: the boxes after a move, resize, nudge or delete. */
    onChange: PropTypes.func,
    /** False for a thumbnail: links and players in the boxes don't take the pointer. */
    interactive: PropTypes.bool,
    /** A copy of a slide that is out of view or fading out: it takes no pointer and holds no block the editor can find. */
    inert: PropTypes.bool,
    /** The sheet's share of a full slide's width (0.5 for one page of a spread on a phone). */
    reference: PropTypes.number,
    page: PropTypes.object,
    pages: PropTypes.object,
    files: PropTypes.object,
    theme: PropTypes.object,
};
